import styled from 'styled-components';

import { useModalObject } from '@wecasa/common/src/hooks/use-modal';

import { translate } from '@wecasa/i18n';

import Text from '@wecasa/web-ui/src/components/base-text/variants/text';
import Separator from '@wecasa/web-ui/src/components/separator/separator';

import WhatIsInstantTaxCreditModal from '../../../../../../tax-credit-modals/what-is-instant-tax-credit-modal';
import WhatIsTaxCreditModal from '../../../../../../tax-credit-modals/what-is-tax-credit-modal';
import { PriceLine, PriceLineLabel, PriceLineTextButton, PriceLineWrapper } from './price-line';

const t = translate('basket');

const FirstSessionLine = styled(PriceLineWrapper)`
  padding-top: 20px;
`;

export type FormattedPlatformFees = {
  firstSessionPrice: string;
  followingSessionsPrice: string;
  feeSurchargePerHour: string;
};

type PriceLinesProps = {
  formattedTaxAmount: string;
  formattedTotalPrice: string;
  formattedPriceAfterTax: string;
  isITCSelectedOrSuccessfullyPaired: boolean;
  isLoading: boolean;
  formattedPlatformFees?: FormattedPlatformFees;
  shouldShowTaxCreditLines: boolean;
};

export const PriceLines = ({
  formattedTaxAmount,
  formattedTotalPrice,
  formattedPriceAfterTax,
  isITCSelectedOrSuccessfullyPaired,
  isLoading,
  formattedPlatformFees,
  shouldShowTaxCreditLines,
}: PriceLinesProps) => {
  const whatIsInstantTaxCreditModal = useModalObject();
  const whatIsTaxCreditModal = useModalObject();

  if (formattedPlatformFees) {
    return (
      <>
        <Separator />
        <FirstSessionLine>
          <PriceLineLabel>
            <Text>{t('.cleaning.services.first_session_total')}</Text>
            <Text.SM>
              {` ${t('.cleaning.services.first_session_setup_fee', { fee: formattedPlatformFees.feeSurchargePerHour })}`}
            </Text.SM>
          </PriceLineLabel>
          <Text>{formattedPlatformFees.firstSessionPrice}</Text>
        </FirstSessionLine>
        <PriceLine
          text={t('.cleaning.services.following_sessions_total')}
          price={formattedPlatformFees.followingSessionsPrice}
          isLoading={isLoading}
        />
      </>
    );
  }

  if (shouldShowTaxCreditLines) {
    return isITCSelectedOrSuccessfullyPaired ? (
      <>
        <WhatIsInstantTaxCreditModal
          isOpen={whatIsInstantTaxCreditModal.isOpen}
          onClose={whatIsInstantTaxCreditModal.close}
        />

        <Separator />
        <PriceLine text={t('.subtotal')} price={formattedTotalPrice} isPriceStrikedOut isLoading={isLoading} />
        <Separator />
        <PriceLineTextButton
          text={t('.cleaning.services.tax.label_itc')}
          price={formattedTaxAmount}
          onPress={whatIsInstantTaxCreditModal.open}
          isLoading={isLoading}
        />
        <Separator />
        <PriceLine text={t('.total')} price={formattedPriceAfterTax} isBold isLoading={isLoading} />
      </>
    ) : (
      <>
        <WhatIsTaxCreditModal isOpen={whatIsTaxCreditModal.isOpen} onClose={whatIsTaxCreditModal.close} />

        <Separator />
        <PriceLine text={t('.total')} price={formattedTotalPrice} isLoading={isLoading} />
        <Separator />
        <PriceLineTextButton
          text={t('.cleaning.services.tax.label')}
          price={formattedTaxAmount}
          onPress={whatIsTaxCreditModal.open}
          isLoading={isLoading}
        />
        <Separator />
        <PriceLine text={t('.after_tax_credit')} price={formattedPriceAfterTax} isBold isLoading={isLoading} />
      </>
    );
  }

  return (
    <>
      <Separator />
      <PriceLine text={t('.total')} price={formattedTotalPrice} isLoading={isLoading} />
    </>
  );
};
